import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  buildEndSessionUrl,
  getAccessToken,
  handleCallback,
  login,
  logout,
  storefrontAuthConfig,
} from './storefront-auth';

interface StorefrontAuthContextValue {
  /** current access token, or null when unauthenticated */
  token: string | null;
  /** kick off the Keycloak Authorization-Code + PKCE redirect */
  login: () => Promise<void>;
  /** complete the `/callback` round-trip; resolves true on success */
  handleCallback: () => Promise<boolean>;
  /** clear the local token AND end the Keycloak SSO session (redirects away) */
  logout: () => void;
}

const StorefrontAuthContext = createContext<StorefrontAuthContextValue | null>(
  null
);

export function StorefrontAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAccessToken());

  const doLogin = useCallback(() => login(storefrontAuthConfig()), []);

  const doHandleCallback = useCallback(async () => {
    const ok = await handleCallback(storefrontAuthConfig());
    setToken(getAccessToken());
    return ok;
  }, []);

  const doLogout = useCallback(() => {
    // Build the end-session URL BEFORE clearing (it wants the stored id_token).
    const endSession = buildEndSessionUrl(storefrontAuthConfig());
    logout();
    // Deliberately NO setToken(null) here: clearing React state re-renders the
    // AuthGuard, whose token=null effect fires the PKCE auto-login — and ITS
    // window.location assign (async, after the crypto await) lands AFTER ours,
    // winning the navigation race. With a live Keycloak SSO the user was
    // silently logged straight back in ("Sign out does nothing" — the bug the
    // portal app hit and fixed 2026-08-26, commit dccdd0f5). The storage is
    // cleared above and the page navigates away; Keycloak ends the session and
    // returns to '/', where the guard runs a fresh login against the now-dead
    // SSO and lands on the login form.
    window.location.assign(endSession);
  }, []);

  // Stable context value (the callbacks never change identity): consumers
  // re-render only when the token actually changes.
  const value = useMemo(
    () => ({
      token,
      login: doLogin,
      handleCallback: doHandleCallback,
      logout: doLogout,
    }),
    [token, doLogin, doHandleCallback, doLogout]
  );

  return (
    <StorefrontAuthContext.Provider value={value}>
      {children}
    </StorefrontAuthContext.Provider>
  );
}

export function useStorefrontAuth(): StorefrontAuthContextValue {
  const ctx = useContext(StorefrontAuthContext);
  if (!ctx) {
    throw new Error(
      'useStorefrontAuth must be used within a StorefrontAuthProvider'
    );
  }
  return ctx;
}
